// src/hooks/useDetectorRelations.ts
import { useEffect, useState, useMemo } from "react";

// --- Interfaces (can be imported from a shared types file) ---
interface DetectorObject {
    mac: string;
    ip: string;
    last_active: string;
    online: boolean;
    status: {
        site_name: string;
        gps_fix: boolean;
        gps_lat: number;
        gps_long: number;
        up_since: string;
        memory: string;
        fw_version: string;
        test_mode: boolean;
        thr: string;
        linux: boolean;
        sim_card_no?: string;
        dacq_algo: string | boolean,
        peak_algo: string | boolean,
        csv_algo: string | boolean,
        file_algo: string | boolean
    };
}

interface DetectorCodeObject {
    code: string;
    coeff: string;
    created_at: string;
    expiration_date: string;
    id: string;
    lat: string;
    lon: string;
    name: string;
    receiver: string;
    reg: string;
}

interface SubAssemblyPart {
    part: number;
    quantity_required: number;
}

interface SubAssembly {
    id: number;
    name: string;
    parts: SubAssemblyPart[];
    subPicture?: any;
    subSOP?: any;
}

interface DetectorSubAssembly {
    subassembly: number | SubAssembly;
    quantity_required: number;
}

interface Detector {
    id: number;
    name: string;
    version: string;
    subassemblies: DetectorSubAssembly[];
}

interface BuiltSubAssemblies {
    id: string;
    built_at: string;
    built_by: string;
    status: string;
    subassembly: number;
    used_by: string;
}

interface BuiltDetectors {
    mac_address: string;
    code?: string;
    built_at: string;
    assembled_by: string | number;
    status: string;
    detector: number | null;
    subassemblies: string[];
}

// Helper function to normalize MAC addresses
function normalizeMac(mac: string): string {
    return mac?.toLowerCase().replace(/^(0x|000)/, "") || "";
}

// --- Hook Implementation ---
export function useDetectorRelations() {
    // Raw data
    const [detectorDataMonitoring, setDetectorDataMonitoring] = useState<DetectorObject[]>([]);
    const [detectorCodeObj, setDetectorCodeObj] = useState<DetectorCodeObject[]>([]);
    const [subAssemblies, setSubAssemblies] = useState<SubAssembly[]>([]);
    const [detectors, setDetectors] = useState<Detector[]>([]);
    const [builtDetectors, setBuiltDetectors] = useState<BuiltDetectors[]>([]);
    const [builtSubAssemblies, setBuiltSubAssemblies] = useState<BuiltSubAssemblies[]>([]);

    // ---- Fetchers ----
    useEffect(() => {
        const fetchMonitoring = async () => {
            try {
                const response = await fetch("/apidet/detector/monitoring", { credentials: "same-origin" });
                if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
                const data = await response.json();

                let alljson: any = {};
                if (data.dataUSA && !data.dataUSA.error) {
                    const cleanedString = data.dataUSA.replace(/\\n/g, "").trim();
                    alljson = JSON.parse(cleanedString);
                }

                const combined = [
                    ...Object.values(alljson).flat(),
                ].filter((d): d is DetectorObject => !!d && typeof d === "object" && "mac" in d);

                setDetectorDataMonitoring(combined);
            } catch (err) {
                console.error("Error fetching monitoring:", err);
                setDetectorDataMonitoring([]);
            }
        };

        fetchMonitoring();
        const interval = setInterval(fetchMonitoring, 5000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        fetch("/portal/api/relay_fnn_network/", { credentials: "same-origin" })
            .then((res) => res.json())
            .then((data) => setDetectorCodeObj(data))
            .catch((err) => console.error("Error fetching detector codes:", err));

        fetch("/api/inventory_subassemblies/", { credentials: "same-origin" })
            .then((res) => res.json())
            .then((data) => setSubAssemblies(data))
            .catch((err) => console.error("Error fetching subassemblies:", err));

        fetch("/api/inventory_detectors/", { credentials: "same-origin" })
            .then((res) => res.json())
            .then((data) => setDetectors(data))
            .catch((err) => console.error("Error fetching detectors:", err));

        fetch("/api/inventory_built_subs/", { credentials: "same-origin" })
            .then((res) => res.json())
            .then((data) => setBuiltSubAssemblies(data))
            .catch((err) => console.error("Error fetching built subassemblies:", err));

        fetch("/api/inventory_built_dets/", { credentials: "same-origin" })
            .then((res) => res.json())
            .then((data) => setBuiltDetectors(data))
            .catch((err) => console.error("Error fetching built detectors:", err));
    }, []);

    // ---- Derived / Connected Relationships ----
    const fullDetectors = useMemo(() => {
        const now = new Date();

        // Create a filtered version of detectorCodeObj with only non-expired detectors
        const validDetectorCodes = detectorCodeObj.filter((c) => {
            if (!c.expiration_date) return true; // No expiration date → valid
            const exp = new Date(c.expiration_date);
            return exp >= now; // Keep if not expired
        });

        // If multiple entries share the same receiver, prefer the most recent non-expired one
        const latestValidCodes = Object.values(
            validDetectorCodes.reduce((acc, c) => {
                const receiverKey = normalizeMac(c.receiver);
                const existing = acc[receiverKey];
                if (
                    !existing ||
                    new Date(c.created_at) > new Date(existing.created_at)
                ) {
                    acc[receiverKey] = c;
                }
                return acc;
            }, {} as Record<string, typeof detectorCodeObj[number]>)
        );

        return detectorDataMonitoring.map((mon) => {
            // Match network code — now only with valid (non-expired) codes
            // Normalize both MACs to handle 0x and 000 prefixes
            const normalizedMonMac = normalizeMac(mon.mac);
            const codeEntry = latestValidCodes.find(
                (c) => normalizeMac(c.receiver) === normalizedMonMac
            );

            // Match built detector with validation
            const built = builtDetectors.find((b) => {
                if (b.mac_address !== mon.mac) return false;
                if (!b.code) return true; // MAC matches, no code → valid
                if (codeEntry && b.code === codeEntry.code) return true; // MAC + code match → valid
                return false; // invalid
            });

            // Get detector BOM
            const detBom = built ? detectors.find((d) => d.id === built.detector) : undefined;

            // Get built subassemblies (resolved)
            const builtSubs = built?.subassemblies
                ?.map((subId) => builtSubAssemblies.find((s) => s.id === subId))
                .filter(Boolean) as BuiltSubAssemblies[];

            // Map to include actual SubAssembly object as subsBom
            const subsBom = builtSubs?.map((bs) => {
                const sub = subAssemblies.find((s) => s.id === bs.subassembly);
                return { ...bs, subassembly: sub };
            });

            return {
                monitoring: mon,
                code: codeEntry,
                built,
                detectorBom: detBom,
                builtSubAssemblies: builtSubs,
                subsBom,
            };
        });
    }, [detectorDataMonitoring, detectorCodeObj, builtDetectors, builtSubAssemblies, detectors, subAssemblies]);

    return {
        detectorDataMonitoring,
        detectorCodeObj,
        subAssemblies,
        detectors,
        builtDetectors,
        builtSubAssemblies,
        fullDetectors,
    };
}